import { useEffect, useRef } from 'react';

import type {
  DescriptionPage_CourseType as CourseType,
  DescriptionPage_DifficultyLevel as DifficultyLevel,
  DescriptionPage_ProductVariant as ProductVariant,
} from '__generated__/graphql-types';

import cookie from 'js/lib/cookie';
import useRouter from 'js/lib/useRouter';

import { AUTOCOMPLETE_PHOTO_SIZE, CourseIcon } from 'bundles/search-common/constants/autocomplete';
import { saveRecentlyViewed } from 'bundles/search-common/utils/SearchUtils';
import { useCareerRolesData } from 'bundles/unified-description-page-common/contexts/CareerRolesDataContext';
import type { PartnerFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import { setOrUpdateRecentlyViewedXDP } from 'bundles/unified-description-page-common/utils/setOrUpdateRecentlyViewedXDP';

export type RecentlyViewedData = {
  name?: string;
  slug?: string;
  partners?: Array<PartnerFragment> | null;
  photoUrl?: string;
  cardImageUrl?: string | null;
  id?: string;
  difficulty?: DifficultyLevel | null;
  productType?: CourseType | ProductVariant | null;
  careerGrowthInPercentage?: number | null;
  medianEntryLevelSalary?: number | null;
  jobOpeningsInUs?: number | null;
};

function useLogRecentlyViewed({
  name,
  slug,
  partners,
  photoUrl,
  cardImageUrl,
  id,
  difficulty,
  productType,
  careerGrowthInPercentage,
  medianEntryLevelSalary,
  jobOpeningsInUs,
}: RecentlyViewedData) {
  const hasRunSet = useRef(false);
  const hasRunSave = useRef(false);
  const path = useRouter().location.pathname;
  const { data: careerRoles, loading: careerRolesLoading } = useCareerRolesData();

  useEffect(() => {
    const sessionId = cookie.get('__204u');

    if (sessionId && id && !hasRunSet.current) {
      setOrUpdateRecentlyViewedXDP(id, sessionId);
      hasRunSet.current = true;
    }
  }, [id]);

  useEffect(() => {
    if (partners && name && !hasRunSave.current && !careerRolesLoading) {
      const careerRolesLength = careerRoles?.length || 0;
      saveRecentlyViewed({
        id,
        name,
        path,
        cardImageUrl,
        imageIconWithSize: photoUrl ? { imageUrl: photoUrl, size: AUTOCOMPLETE_PHOTO_SIZE } : CourseIcon,
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        partnerName: partners && partners[0].name,
        slug,
        // @ts-expect-error URL: string
        partnerLogo: partners && (partners[0].squareLogo || CourseIcon),
        difficulty,
        productType,
        isPathwayProduct: careerRolesLength > 0,
        careerGrowthInPercentage,
        medianEntryLevelSalary,
        jobOpeningsInUs,
      });
      hasRunSave.current = true;
    }
  }, [
    difficulty,
    id,
    name,
    partners,
    path,
    photoUrl,
    productType,
    slug,
    careerRolesLoading,
    careerRoles,
    careerGrowthInPercentage,
    medianEntryLevelSalary,
    jobOpeningsInUs,
    cardImageUrl,
  ]);
}

export default useLogRecentlyViewed;
